import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Clock, ArrowUpRight } from "lucide-react";
import { JOURNEY_STAGES, PROGRESS_PERCENT } from "@/lib/journey";

const Icon = ({ status }) => {
  if (status === "done") return <CheckCircle2 size={14} className="text-[#F47A20]" />;
  if (status === "active") return <Loader2 size={14} className="text-[#F47A20] animate-spin" />;
  return <Clock size={14} className="text-white/35" />;
};

const completed = JOURNEY_STAGES.filter((s) => s.status === "done").length;

export const DevelopmentTracker = () => {
  return (
    <div data-testid="development-tracker" className="mt-14 p-6 md:p-8 bg-[#0E0E11] border border-white/10 rounded-sm">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="serial text-[10px] tracking-[0.25em] text-[#F47A20] font-bold mb-1.5">
            OUTLAW V1 &middot; DEVELOPMENT PROGRESS
          </div>
          <div className="text-white text-lg md:text-xl font-bold leading-tight">
            From concept to highway, in the open.
          </div>
          <p className="serial text-[10px] text-white/45 mt-2">
            Each completed milestone opens its Founder Journal entry.
          </p>
        </div>
        <div className="text-right">
          <div data-testid="dev-tracker-percent" className="h-display text-3xl md:text-4xl text-white leading-none">{PROGRESS_PERCENT}<span className="text-[#F47A20] text-2xl">%</span></div>
          <div className="serial text-[9px] tracking-[0.25em] text-white/45 mt-1">
            {completed} OF {JOURNEY_STAGES.length} PHASES COMPLETE
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative h-1 bg-white/8 rounded-full overflow-hidden mb-8">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${PROGRESS_PERCENT}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, ease: [0.2, 0.7, 0.2, 1] }}
          className="absolute inset-y-0 left-0 bg-[#F47A20]"
        />
      </div>

      {/* Stages */}
      <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {JOURNEY_STAGES.map((s) => {
          const clickable = (s.status === "done" || s.status === "active") && s.slug;
          const tone =
            s.status === "done"
              ? "border-[#F47A20]/35 bg-[#F47A20]/[0.04] text-white"
              : s.status === "active"
              ? "border-[#F47A20] bg-[#F47A20]/10 text-white shadow-[0_0_0_1px_rgba(244,122,32,0.25)]"
              : "border-white/8 bg-[#0A0A0C] text-white/55";
          const hoverTone = clickable
            ? "hover:border-[#F47A20] hover:bg-[#F47A20]/[0.09] cursor-pointer"
            : "";
          const labelTone = s.status === "pending" ? "text-white/55" : "text-white";

          const body = (
            <div className={`p-3.5 border rounded-sm flex items-start gap-2.5 transition-colors ${tone} ${hoverTone}`}>
              <span className="flex-shrink-0 mt-0.5">
                <Icon status={s.status} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="serial text-[9px] tracking-[0.25em] text-white/40">
                    PHASE {String(s.idx).padStart(2, "0")}
                  </div>
                  {clickable && (
                    <ArrowUpRight size={11} className="text-[#F47A20]/65 group-hover:text-[#F47A20] transition-colors" />
                  )}
                </div>
                <div className={`text-sm font-bold leading-snug mt-1 ${labelTone}`}>{s.trackerLabel}</div>
                {s.status === "active" && (
                  <div className="serial text-[9px] tracking-[0.2em] text-[#F47A20] font-bold mt-1.5">CURRENT PHASE</div>
                )}
                {clickable && (
                  <div className="serial text-[9px] tracking-[0.2em] text-white/45 mt-1.5">
                    READ JOURNAL ENTRY &rarr;
                  </div>
                )}
              </div>
            </div>
          );

          if (clickable) {
            return (
              <li
                key={s.key}
                data-testid={`dev-stage-${s.key}`}
                data-status={s.status}
                data-slug={s.slug || ""}
              >
                <Link
                  to={`/journal/${s.slug}`}
                  data-testid={`dev-stage-${s.key}-link`}
                  aria-label={`Open ${s.trackerLabel} journal entry`}
                  className="group block"
                >
                  {body}
                </Link>
              </li>
            );
          }

          return (
            <li
              key={s.key}
              data-testid={`dev-stage-${s.key}`}
              data-status={s.status}
              data-slug=""
            >
              {body}
            </li>
          );
        })}
      </ol>

      <p className="serial text-[10px] text-white/40 mt-6">
        Updated as each phase completes. VIPs receive a Founder Journal update at every milestone.
      </p>
    </div>
  );
};

export default DevelopmentTracker;
